// =============================================================================
// SHARED VOICE-SESSION TYPES — extracted from apps/web/src/lib/api.ts (the
// backend agent-session contract) so any app on the same live ElevenLabs +
// Studio backend (careeropt-api's POST /api/agent-session) can consume the
// session hook without depending on apps/web's own api.ts module.
//
// COPY-then-adapt: this is a transcription of the WIRE SHAPE apps/web/src/
// lib/api.ts already defines (lines ~522-901), not a new contract. apps/web's
// originals are UNTOUCHED — this package is the new shared home going forward,
// consumed today by apps/throughline only.
// =============================================================================

/** The five DNA facet kinds a landed facet can carry. */
export type DnaFacetType = 'accomplishment' | 'knowledge' | 'lesson' | 'thread' | 'driver';

/** landFacet (agent → client): a facet caught mid-conversation. Carried through
 *  for contract completeness (the client-tool registration below expects it),
 *  even though the Throughline B-2 orb does not yet render facet flights —
 *  that lands with the Refine tab's live wiring (a later brief). */
export interface LandFacetPayload {
  facetId: string;
  facetType: DnaFacetType;
  content: string;
  isNormalToMe: boolean;
  targetRoleId: string | null;
  targetAchievementId: string | null;
  originLabel: string | null;
  confidence?: number;
}

/** focusCard (client → agent): fired when the user taps a card mid-session so
 *  the agent pivots to it. */
export interface FocusCardPayload {
  anchorType: 'achievement' | 'role' | 'origins';
  anchorId: string | null;
}

/** rehomeCard (agent → client): the spoken-correction PROPOSAL (the
 *  correction loop, §3.3). A PROPOSAL only — the agent never auto-applies;
 *  the consuming app surfaces a confirm affordance and the user's explicit
 *  yes triggers the gated re-home via its own API call. Mirrored verbatim
 *  from apps/web/src/lib/api.ts RehomeCardPayload so a backend proposal
 *  coerces identically regardless of which app is listening. */
export interface RehomeCardPayload {
  achievementId: string;
  achievementHeadline: string;
  proposedTarget: {
    roleId: string | null;
    roleEventId: string | null;
    label: string;
  };
  spokenCorrection: string;
}

/** The client-tool names, frozen. Must match the ElevenLabs agent config AND
 *  the React SDK registration verbatim or tool calls will not route. */
export const CLIENT_TOOL_NAMES = {
  landFacet: 'landFacet',
  focusCard: 'focusCard',
  rehomeCard: 'rehomeCard',
} as const;

/** Response from POST /api/agent-session. The client passes conversationToken
 *  to startSession({ conversationToken, connectionType: 'webrtc' }) and
 *  applies overrides + dynamicVariables. The ElevenLabs key never reaches
 *  the client. */
export interface AgentSessionResponse {
  sessionId: string;
  /** Echoed back so the client forwards it in customLlmExtraBody. */
  userId: string;
  conversationToken: string;
  agentId: string;
  overrides: {
    agent?: {
      prompt?: { prompt: string };
      firstMessage?: string;
      language?: string;
    };
    tts?: { voiceId?: string };
  };
  dynamicVariables: Record<string, string | number | boolean>;
}

/** Request body for POST /api/agent-session: which card/role the session
 *  opens on. All optional (an unanchored open session is valid). */
export interface AgentSessionRequest {
  anchorType?: 'achievement' | 'role' | 'origins' | 'gap';
  anchorId?: string | null;
  sessionId?: string;
  gap?: { text: string; jobId: string };
}

/** The function shape a consuming app supplies to mint a session — its OWN
 *  apiFetch wrapper bound to POST /agent-session, so this package never
 *  imports either app's api.ts (no cross-app coupling, no cyclic dep). */
export type MintSession = (input: AgentSessionRequest) => Promise<AgentSessionResponse>;
